import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api, mediaUrl } from '@/lib/api';
import { HOME } from '@/constants/testIds';
import FeaturedHeadliner from '@/components/FeaturedHeadliner';
import { Quote } from 'lucide-react';

/**
 * ConfigurableHero
 * ----------------
 * Consumes the Homepage Director's Hero config (via /api/homepage or a passed-in
 * blueprint) and renders the homepage hero. Right column now renders the
 * FeaturedHeadliner component (replacing the fixed Now Playing card).
 *
 * Props:
 *   blueprint  — optional; when provided, the Hero uses this blueprint instead
 *                of fetching /api/homepage. Used by the full-page preview route.
 *   source     — 'live' | 'draft' — controls Featured Headliner resolver.
 */
export function ConfigurableHero({ blueprint, source = 'live' }) {
  const [config, setConfig] = useState(blueprint || null);
  const [dailyDrop, setDailyDrop] = useState(null);

  useEffect(() => {
    if (blueprint) { setConfig(blueprint); return; }
    api.get('/homepage').then(({ data }) => setConfig(data)).catch(() => setConfig(null));
  }, [blueprint]);

  useEffect(() => {
    // Daily Drop is not part of the Hero config yet — it stays sourced from
    // its existing endpoint so the "SUBSCRIBE TO THE SIGNAL" note remains
    // intact.
    api.get('/home/feed').then(({ data }) => setDailyDrop(data?.daily_drop || null)).catch(() => {});
  }, []);

  const hero = (config && config.hero) || {};
  if (hero.visible === false) return null;

  const spacing = hero.section_spacing || 'spacious';
  const paddingCls = spacing === 'compact'
    ? 'pt-8 pb-8 sm:pt-10 sm:pb-10'
    : spacing === 'default'
      ? 'pt-10 pb-10 sm:pt-14 sm:pb-12'
      : 'pt-10 sm:pt-16 lg:pt-20 pb-12 sm:pb-16';

  const overlayColor = hero.background_overlay_color || 'rgba(7,8,15,0.55)';
  const overlayOpacity = typeof hero.background_overlay_opacity_pct === 'number'
    ? Math.max(0, Math.min(1, hero.background_overlay_opacity_pct / 100))
    : 0.55;
  const bgUrl = hero.background_media_url
    ? mediaUrl(hero.background_media_url)
    : 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85';
  const bgPos = hero.background_position || 'center';

  const primary = hero.primary_cta || null;
  const secondary = hero.secondary_cta || null;

  const primaryClassFor = (s) => s === 'gold'
    ? 'pg-btn-primary'
    : s === 'ghost'
      ? 'pg-btn-ghost'
      : 'pg-btn-neon';

  return (
    <section className="relative w-full overflow-hidden" data-testid="configurable-hero">
      <div className="absolute inset-0" aria-hidden>
        {hero.background_media_type === 'video' && hero.background_media_url ? (
          <video
            src={mediaUrl(hero.background_media_url)}
            autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: bgPos, opacity: 0.55 }}
          />
        ) : (
          <div
            className="absolute inset-0 bg-cover"
            style={{
              backgroundImage: `url('${bgUrl}')`,
              backgroundPosition: bgPos,
              opacity: 0.55,
              filter: 'saturate(1.05)',
            }}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(60% 60% at 18% 30%, rgba(255,62,165,0.20), transparent 60%), radial-gradient(50% 60% at 82% 25%, rgba(25,194,142,0.18), transparent 60%), linear-gradient(180deg, ${overlayColor} 0%, rgba(7,8,15,0.95) 95%)`,
            opacity: overlayOpacity < 1 ? Math.min(1, overlayOpacity + 0.3) : 1,
          }}
        />
      </div>

      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 ${paddingCls}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left — editorial headline */}
          <div className="lg:col-span-7">
            {hero.welcome_copy && (
              <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
                <motion.p
                  initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  className="pg-label"
                  style={{ textShadow: '0 2px 14px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)' }}
                  data-testid="hero-welcome-copy"
                >
                  {hero.welcome_copy}
                </motion.p>
              </span>
            )}
            {hero.main_heading && (
              <motion.h1
                data-testid={HOME.heroHeadline}
                initial={{ opacity: 1, y: 18, filter: 'blur(8px)' }} animate={{ opacity: 1, y: 0, filter: 'blur(0)' }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
                className="font-display text-6xl sm:text-7xl lg:text-[120px] xl:text-[140px] leading-[0.95] mt-3 pg-neon-text"
              >
                {hero.main_heading}
              </motion.h1>
            )}
            {hero.supporting_copy && (
              <div className="mt-6">
                <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
                  <motion.p
                    initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
                    className="font-mono text-[12px] sm:text-sm tracking-[0.32em] text-[var(--pg-ink-muted)] uppercase"
                    style={{ textShadow: '0 1px 8px rgba(0,0,0,0.85)' }}
                    data-testid="hero-supporting-copy"
                  >
                    {hero.supporting_copy}
                  </motion.p>
                </span>
              </div>
            )}

            {(primary || secondary) && (
              <motion.div
                initial={{ opacity: 1, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                {primary?.label && (
                  <a
                    data-testid={HOME.heroExploreButton}
                    href={primary.href || '#channels'}
                    className={primaryClassFor(primary.style || 'neon')}
                  >
                    {primary.label}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                  </a>
                )}
                {secondary?.label && (
                  <a
                    data-testid="hero-secondary-cta"
                    href={secondary.href || '#'}
                    className={primaryClassFor(secondary.style || 'ghost')}
                  >
                    {secondary.label}
                  </a>
                )}
                <span className="pg-onair"><span className="pg-onair-dot" /> ON AIR</span>
              </motion.div>
            )}

            {dailyDrop && (
              <motion.div
                initial={{ opacity: 1, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}
                className="mt-10"
              >
                <DailyDropCard note={dailyDrop} />
              </motion.div>
            )}
          </div>

          {/* Right — Featured Headliner (replaces the fixed Now Playing card) */}
          <motion.div
            initial={{ opacity: 1, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <FeaturedHeadliner source={source} configOverride={blueprint ? blueprint.featured_headliner : undefined} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function DailyDropCard({ note }) {
  return (
    <div className="pg-card p-5 sm:p-6 max-w-sm" style={{ borderColor: 'rgba(255,62,165,0.25)' }}>
      <div className="flex items-center gap-2">
        <span className="pg-text-glow-pink font-mono text-[10px] tracking-[0.32em] uppercase">Daily Drop</span>
      </div>
      <div className="mt-3 flex items-start gap-3">
        <Quote size={20} className="text-[var(--pg-neon-pink)] shrink-0 mt-1" />
        <p className="text-[var(--pg-ink)] text-base leading-relaxed font-serif">
          {note?.body || 'The light just hit the room. Pull up a chair — G is about to start.'}
        </p>
      </div>
      {note?.title && <p className="text-xs text-[var(--pg-ink-muted)] mt-3 font-mono">{note.title}</p>}
      <p className="text-xs text-[var(--pg-gold-2)] mt-3 font-mono">— G</p>
    </div>
  );
}
